import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import "./NavMiniFlyout.css";

const CLOSE_DELAY_MS = 150;
const FLYOUT_VIEWPORT_PAD = 8;
const FLYOUT_MIN_HEIGHT = 160;

function getFlyoutLayout(anchorEl) {
  if (!anchorEl || typeof window === "undefined") {
    return {
      placement: "right-start",
      maxHeight: "min(85vh, calc(100vh - 16px))",
    };
  }
  const rect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openUpward = rect.top + rect.height / 2 > window.innerHeight / 2;
  const available = openUpward
    ? Math.max(spaceAbove + rect.height, spaceAbove)
    : Math.max(spaceBelow + rect.height, spaceBelow);
  return {
    placement: openUpward ? "right-end" : "right-start",
    maxHeight: Math.max(
      FLYOUT_MIN_HEIGHT,
      Math.min(window.innerHeight - FLYOUT_VIEWPORT_PAD * 2, available - FLYOUT_VIEWPORT_PAD)
    ),
  };
}

function NavMiniFlyout({
  drawerOpen,
  menuOpen,
  onMenuToggle,
  selected,
  buttonClassName,
  icon,
  label,
  labelClassName,
  labelStyle,
  showChevron,
  children,
  flyoutMinWidth,
  anchorComponent,
  anchorTo,
  onAnchorClick,
  bareFlyout,
  forceFlyout,
}) {
  const anchorRef = useRef(null);
  const paperRef = useRef(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutLayout, setFlyoutLayout] = useState({
    placement: "right-start",
    maxHeight: "min(85vh, calc(100vh - 16px))",
  });
  const closeTimerRef = useRef(null);
  const useFlyout = forceFlyout || !drawerOpen;
  const clickToToggle = forceFlyout && drawerOpen;

  const prepareFlyout = () => {
    setFlyoutLayout(getFlyoutLayout(anchorRef.current));
  };

  useEffect(() => {
    if (!flyoutOpen) {
      return undefined;
    }
    const onReposition = () => {
      setFlyoutLayout(getFlyoutLayout(anchorRef.current));
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [flyoutOpen]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const canShowFlyout = useFlyout && !!(children || label);

  const handleMouseEnter = () => {
    if (!canShowFlyout || clickToToggle) {
      return;
    }
    clearCloseTimer();
    prepareFlyout();
    setFlyoutOpen(true);
  };

  const handleMouseLeave = () => {
    if (!canShowFlyout || clickToToggle) {
      return;
    }
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setFlyoutOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const handleClick = (event) => {
    if (onAnchorClick) {
      onAnchorClick(event);
    }
    if (clickToToggle) {
      clearCloseTimer();
      prepareFlyout();
      setFlyoutOpen((open) => !open);
      return;
    }
    // The whole row owns the menu. A link row still navigates; the caret is
    // only a visual hint, not a separate control.
    if (drawerOpen && onMenuToggle) {
      onMenuToggle(event);
    }
  };

  const handleClickAway = (event) => {
    if (!clickToToggle || !flyoutOpen) {
      return;
    }
    const target = event.target;
    if (anchorRef.current && anchorRef.current.contains(target)) {
      return;
    }
    if (paperRef.current && paperRef.current.contains(target)) {
      return;
    }
    setFlyoutOpen(false);
  };

  const buttonProps = {
    ref: anchorRef,
    className: buttonClassName,
    selected: selected || (clickToToggle && flyoutOpen),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleClick,
    "aria-expanded": showChevron
      ? !!(clickToToggle || useFlyout ? flyoutOpen : menuOpen)
      : undefined,
  };

  if (anchorComponent) {
    buttonProps.component = anchorComponent;
    if (anchorTo) {
      buttonProps.to = anchorTo;
    }
  }

  const chevronIcon = !drawerOpen || !showChevron
    ? null
    : forceFlyout
      ? (
        <ChevronRight className={labelClassName} />
      )
      : menuOpen
        ? (
          <ExpandLess className={labelClassName} />
        )
        : (
          <ExpandMore className={labelClassName} />
        );
  const chevron = chevronIcon ? (
    <span className="nav-mini-flyout-chevron" data-nav-chevron="true" aria-hidden="true">
      {chevronIcon}
    </span>
  ) : null;

  const flyout = canShowFlyout ? (
    <Popper
      open={flyoutOpen}
      anchorEl={anchorRef.current}
      placement={flyoutLayout.placement}
      strategy="fixed"
      disablePortal={false}
      container={typeof document !== "undefined" ? document.body : undefined}
      modifiers={[
        {
          name: "offset",
          options: { offset: [0, 8] },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["right-end", "right-start", "left-end", "left-start"],
            padding: FLYOUT_VIEWPORT_PAD,
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: FLYOUT_VIEWPORT_PAD,
            altAxis: true,
            tether: false,
            boundary: "clippingParents",
            rootBoundary: "viewport",
          },
        },
      ]}
      style={{ zIndex: 1500 }}
    >
      <div
        ref={paperRef}
        style={{
          maxHeight: flyoutLayout.maxHeight,
          display: "flex",
          flexDirection: "column",
        }}
      >
      <Paper
        className={
          'nav-mini-flyout-panel' + (bareFlyout ? ' is-bare' : '')
        }
        elevation={8}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(event) => {
          if (event.target.closest && event.target.closest('a')) {
            setFlyoutOpen(false);
          }
        }}
        sx={{
          minWidth: children ? flyoutMinWidth : 148,
          maxWidth: bareFlyout ? 640 : 400,
          display: "flex",
          flexDirection: "column",
          maxHeight: flyoutLayout.maxHeight,
          overflow: "hidden",
          ...(bareFlyout
            ? { padding: 0, backgroundColor: "transparent" }
            : {}),
        }}
      >
        {label && !bareFlyout ? (
          <div className="nav-mini-flyout-title">{label}</div>
        ) : null}
        {children ? (
          <div
            className={
              'nav-mini-flyout-body' + (bareFlyout ? ' is-bare' : '')
            }
            onWheel={(event) => event.stopPropagation()}
          >
            {children}
          </div>
        ) : null}
      </Paper>
      </div>
    </Popper>
  ) : null;

  const button = (
    <ListItemButton {...buttonProps}>
      {icon}
      {drawerOpen ? (
        <ListItemText
          primary={label}
          className={labelClassName}
          style={labelStyle}
        />
      ) : null}
      {chevron}
    </ListItemButton>
  );

  if (!forceFlyout) {
    return (
      <React.Fragment>
        {button}
        {drawerOpen ? (
          <Collapse in={menuOpen} timeout="auto" unmountOnExit>
            {children}
          </Collapse>
        ) : (
          flyout
        )}
      </React.Fragment>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
      <div>
        {button}
        {flyout}
      </div>
    </ClickAwayListener>
  );
}

NavMiniFlyout.propTypes = {
  drawerOpen: PropTypes.bool,
  menuOpen: PropTypes.bool,
  onMenuToggle: PropTypes.func,
  selected: PropTypes.bool,
  buttonClassName: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.node,
  labelClassName: PropTypes.string,
  labelStyle: PropTypes.object,
  showChevron: PropTypes.bool,
  children: PropTypes.node,
  flyoutMinWidth: PropTypes.number,
  anchorComponent: PropTypes.elementType,
  anchorTo: PropTypes.string,
  onAnchorClick: PropTypes.func,
  bareFlyout: PropTypes.bool,
  forceFlyout: PropTypes.bool,
};

NavMiniFlyout.defaultProps = {
  drawerOpen: true,
  menuOpen: false,
  showChevron: true,
  flyoutMinWidth: 280,
  bareFlyout: false,
  forceFlyout: false,
};

export default NavMiniFlyout;



// WEBPACK FOOTER //
// ./src/components/leftnav/NavMiniFlyout.js